export const INITIAL_STATE = {
  fetching: true,
  error: false,
  replying: false,
  localStateComments: new Set(),
  data: {
    _id: null,
    image: '',
    mediaUrl: '',
    thumbnail: '',
    mediaType: 'image',
    metadata: null,
    caption: '',
    author: null,
    date: null,
    postVotes: [],
    comments: [],
    commentReplies: [],
    monetization: null,
  },
};

export const postDialogReducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_POST_FAILURE': {
      return { ...state, fetching: false, error: action.payload };
    }
    case 'FETCH_POST_SUCCESS': {
      const {
        comments = [],
        commentCount: incomingCount,
        totalCount,
      } = action.payload.commentData || {};
      const commentCount =
        typeof incomingCount === 'number'
          ? incomingCount
          : typeof totalCount === 'number'
          ? totalCount
          : typeof action.payload.commentCount === 'number'
          ? action.payload.commentCount
          : 0;
      return {
        ...state,
        fetching: false,
        error: false,
        data: {
          ...action.payload,
          commentReplies: [],
          comments,
          commentCount,
        },
      };
    }
    case 'VOTE_POST': {
      const { currentUser, postId, dispatch } = action.payload;
      let postVotes = Array.isArray(state.data.postVotes)
        ? [...state.data.postVotes]
        : [];
      const hasLocalVote = postVotes.some(
        (vote) => vote.author === currentUser._id
      );
      const hasStoredReaction = state.data.reactionType === 'like';
      const liked = hasLocalVote || hasStoredReaction;
      let nextReactionType;

      if (!liked) {
        postVotes.push({ author: currentUser._id });
        nextReactionType = 'like';
      } else {
        postVotes = postVotes.filter((vote) => vote.author !== currentUser._id);
        nextReactionType = null;
      }

      let nextLikeCount = state.data.likeCount ?? 0;
      let likeCountChanged = false;
      if (hasStoredReaction && !hasLocalVote && nextReactionType === null) {
        nextLikeCount = Math.max(0, nextLikeCount - 1);
        likeCountChanged = true;
      }

      const normalizedPostId =
        typeof postId === 'string' ? postId : String(postId ?? '');
      dispatch &&
        dispatch({
          type: 'SET_POST_VOTES_COUNT',
          payload: {
            postId: normalizedPostId,
            postVotes,
            voteCount: postVotes.length,
            reactionType: nextReactionType,
            ...(likeCountChanged ? { likeCount: nextLikeCount } : {}),
          },
        });

      return {
        ...state,
        data: {
          ...state.data,
          postVotes,
          reactionType: nextReactionType,
          likeCount: likeCountChanged ? nextLikeCount : state.data.likeCount,
        },
      };
    }
    case 'VOTE_COMMENT': {
      const { commentId, currentUser } = action.payload;
      const comments = JSON.parse(JSON.stringify(state.data.comments));
      const commentIndex = comments.findIndex(
        (comment) => comment._id === commentId
      );
      const liked = !!comments[commentIndex].commentVotes.find(
        (vote) => vote.author === currentUser._id
      );

      if (!liked) {
        comments[commentIndex].commentVotes.push({ author: currentUser._id });
      } else {
        comments[commentIndex].commentVotes = comments[
          commentIndex
        ].commentVotes.filter((vote) => vote.author !== currentUser._id);
      }

      return {
        ...state,
        data: {
          ...state.data,
          comments,
        },
      };
    }
    case 'ADD_COMMENT': {
      let comment = action.payload;
      let localStateComments = new Set(state.localStateComments);
      const isSingle = !Array.isArray(comment);
      if (isSingle) {
        localStateComments.add(comment._id);
        comment = [comment];
      }
      return {
        ...state,
        localStateComments,
        data: {
          ...state.data,
          comments: [...state.data.comments, ...comment],
          // Optimistically increment total comment count only for single, locally added comments
          commentCount: isSingle
            ? (state.data.commentCount || 0) + 1
            : state.data.commentCount,
        },
      };
    }
    case 'SET_COMMENTS_TOTAL': {
      const total = action.payload;
      return {
        ...state,
        data: {
          ...state.data,
          commentCount: typeof total === 'number' ? total : state.data.commentCount,
        },
      };
    }
    case 'REMOVE_COMMENT': {
      const commentId = action.payload;
      const localStateComments = new Set(state.localStateComments);
      localStateComments.has(commentId) && localStateComments.delete(commentId);

      return {
        ...state,
        data: {
          ...state.data,
          localStateComments,
          comments: state.data.comments.filter(
            (comment) => comment._id !== commentId
          ),
          commentCount: state.data.commentCount - 1,
        },
      };
    }
    case 'ADD_COMMENT_REPLY': {
      let { comment: newComment, parentCommentId } = action.payload;
      const comments = JSON.parse(JSON.stringify(state.data.comments));
      const parentCommentIndex = comments.findIndex(
        (comment) => comment._id === parentCommentId
      );
      if (!Array.isArray(newComment)) {
        const parentComment = comments[parentCommentIndex];
        // Add a single comment object into an array
        // so we can spread it into the commentReplies array
        newComment = [newComment];
        parentComment.commentReplies = parentComment.commentReplies
          ? parentComment.commentReplies + 1
          : 1;
      }
      return {
        ...state,
        data: {
          ...state.data,
          comments,
          commentReplies: [...state.data.commentReplies, ...newComment],
        },
      };
    }
    case 'SET_COMMENT_REPLIES_TOTAL': {
      const { parentCommentId, totalCount } = action.payload;
      const comments = JSON.parse(JSON.stringify(state.data.comments));
      const idx = comments.findIndex((c) => c._id === parentCommentId);
      if (idx >= 0 && typeof totalCount === 'number') {
        comments[idx].commentReplies = totalCount;
      }
      return {
        ...state,
        data: {
          ...state.data,
          comments,
        },
      };
    }
    case 'REMOVE_COMMENT_REPLY': {
      const { commentReplyId, parentCommentId } = action.payload;
      const comments = JSON.parse(JSON.stringify(state.data.comments));
      const commentIndex = comments.findIndex(
        (comment) => comment._id === parentCommentId
      );
      comments[commentIndex].commentReplies -= 1;

      return {
        ...state,
        data: {
          ...state.data,
          comments,
          commentReplies: state.data.commentReplies.filter(
            (commentReply) => commentReply._id !== commentReplyId
          ),
        },
      };
    }
    case 'VOTE_COMMENT_REPLY': {
      const { commentReplyId, currentUser } = action.payload;
      const commentReplies = JSON.parse(
        JSON.stringify(state.data.commentReplies)
      );
      const commentReplyIndex = state.data.commentReplies.findIndex(
        (commentReply) => commentReply._id === commentReplyId
      );
      const liked = !!commentReplies[commentReplyIndex].commentReplyVotes.find(
        (vote) => vote.author === currentUser._id
      );
      if (!liked) {
        commentReplies[commentReplyIndex].commentReplyVotes.push({
          author: currentUser._id,
        });
      } else {
        commentReplies[commentReplyIndex].commentReplyVotes = commentReplies[
          commentReplyIndex
        ].commentReplyVotes.filter((vote) => vote.author !== currentUser._id);
      }

      return {
        ...state,
        data: {
          ...state.data,
          commentReplies,
        },
      };
    }
    case 'SET_REPLYING': {
      const { username, commentId } = action.payload || {};
      if (username && commentId) {
        // Avoid re-rendering if commentId and commentUser are the same as previous state
        if (
          state.replying.commentId === commentId &&
          state.replying.commentUser === username
        ) {
          return state;
        }
        return { ...state, replying: { commentUser: username, commentId } };
      } else {
        return { ...state, replying: false };
      }
    }
    default: {
      throw new Error(
        `Invalid action type '${action.type}' passed to postDialogReducer.`
      );
    }
  }
};
